import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, CornerLeftUp, Users } from 'lucide-react';
import { CARD_CLASS, OMS_COLORS, tint } from '../../dashboard/omsDashTheme';
import { ADMIN_EYEBROW_CLASS } from '../adminTheme';
import { formatNumber } from '../../dashboard/utils/formatNumber';
import type { BreakdownEntry } from '../utils/adminMetrics';
import type { AdminScope, AdminScopeActions } from '../hooks/useAdminScope';
import { DeltaBadge, RateTrioBar, RateTrioLegend } from './adminBits';

interface Props {
  scope: AdminScope;
  actions: AdminScopeActions;
  levelLabel: string;
  entries: BreakdownEntry[];
  crumbs: Array<{ label: string; level: 1 | 2 | 3 | 4 | 'current' }>;
  phrase: string;
  loading: boolean;
  onOpenEngineer: (engineerId: string) => void;
}

function Rails({ entries, onSelect }: { entries: BreakdownEntry[]; onSelect: (e: BreakdownEntry) => void }) {
  const rated = entries.filter((e) => e.completionPct !== null);
  const top = rated.slice(0, 3);
  const topKeys = new Set(top.map((e) => e.key));
  const bottom = rated.filter((e) => !topKeys.has(e.key) && (e.completionPct ?? 100) < 60).slice(-3).reverse();
  const rail = (title: string, tone: string, rows: BreakdownEntry[], empty: string) => (
    <div className="rounded-xl border border-[rgba(11,59,46,0.16)] p-2.5" style={{ backgroundColor: tint(tone, 0.05) }}>
      <p className="text-[9px] font-bold uppercase tracking-[0.14em]" style={{ color: tone }}>
        {title}
      </p>
      {rows.length === 0 ? (
        <p className="mt-1.5 text-[10.5px] font-medium" style={{ color: OMS_COLORS.caption }}>
          {empty}
        </p>
      ) : (
        <ul className="mt-1 space-y-0.5">
          {rows.map((row) => (
            <li key={row.key}>
              <button
                type="button"
                onClick={() => onSelect(row)}
                className="flex w-full items-center justify-between gap-2 rounded-lg px-1.5 py-1 text-left transition hover:bg-[rgba(11,59,46,0.05)]"
              >
                <span className="truncate text-[11.5px] font-bold" style={{ color: OMS_COLORS.tealDark }}>
                  {row.name}
                </span>
                <span className="shrink-0 text-[10.5px] font-extrabold tabular-nums" style={{ color: tone }}>
                  {row.completionPct}%
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
  return (
    <div className="flex flex-col gap-3">
      {rail('Top performers', '#1F5A47', top, 'No rated entries yet.')}
      {rail('Needs attention', '#9F1239', bottom, 'Nothing flagged in this window.')}
    </div>
  );
}

/**
 * Drill-down explorer — State → Location → Project → Engineer with the
 * same card anatomy at every level: task counts, the rate-trio micro bar,
 * active engineers and a delta vs the previous window. Drilling here
 * re-filters every chart on the page (the drill lives in the URL).
 */
export default function AdminDrilldownExplorer({
  scope,
  actions,
  levelLabel,
  entries,
  crumbs,
  phrase,
  loading,
  onOpenEngineer,
}: Props) {
  const drill = (entry: BreakdownEntry) => {
    if (entry.kind === 'state') actions.drillToState(entry.key);
    else if (entry.kind === 'location') actions.drillToLocation(entry.key);
    else if (entry.kind === 'project') actions.drillToProject(entry.key);
    else onOpenEngineer(entry.key);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    if (/^(INPUT|SELECT|TEXTAREA)$/.test(target.tagName)) return;
    if (e.key === 'Escape' || e.key === 'Backspace') {
      if (scope.level > 1) {
        e.preventDefault();
        actions.drillUp();
      }
      return;
    }
    if (!['ArrowDown', 'ArrowUp'].includes(e.key)) return;
    const rows = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[data-admin-row]'));
    if (rows.length === 0) return;
    e.preventDefault();
    const index = rows.indexOf(target.closest('[data-admin-row]') as HTMLElement);
    const next = index === -1 ? 0 : (index + (e.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length;
    rows[next]?.focus();
  };

  const levelKey = `${scope.level}-${scope.state ?? ''}-${scope.locationId ?? ''}-${scope.projectId ?? ''}`;

  return (
    <section aria-label="Drill-down explorer" className={`${CARD_CLASS} p-4`} onKeyDown={onKeyDown}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className={ADMIN_EYEBROW_CLASS}>Drill-down explorer · {levelLabel.toLowerCase()}</p>
          <nav aria-label="Drill-down breadcrumb" className="mt-1 flex flex-wrap items-center gap-1">
            {crumbs.map((crumb, i) => (
              <span key={`${crumb.label}-${i}`} className="flex items-center gap-1">
                {i > 0 && <ChevronRight aria-hidden className="h-3.5 w-3.5" style={{ color: '#14606B' }} />}
                <button
                  type="button"
                  disabled={crumb.level === 'current'}
                  onClick={() => crumb.level !== 'current' && actions.drillToLevel(crumb.level)}
                  className={`rounded-lg px-1.5 py-0.5 text-[15px] font-extrabold tracking-tight transition ${
                    crumb.level === 'current' ? 'cursor-default' : 'hover:bg-[rgba(20,96,107,0.08)]'
                  }`}
                  style={{ color: crumb.level === 'current' ? OMS_COLORS.tealDark : '#14606B' }}
                  aria-current={crumb.level === 'current' ? 'page' : undefined}
                >
                  {crumb.label}
                </button>
              </span>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-2">
          {scope.level > 1 && (
            <button
              type="button"
              onClick={actions.drillUp}
              className="flex items-center gap-1 rounded-lg border border-[rgba(11,59,46,0.24)] px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide transition hover:bg-[rgba(11,59,46,0.05)]"
              style={{ color: '#14606B', backgroundColor: '#fcfaf3' }}
            >
              <CornerLeftUp className="h-3 w-3" /> Up one level
            </button>
          )}
          <RateTrioLegend />
        </div>
      </div>

      {loading && entries.length === 0 ? (
        <div className="mt-3 space-y-2">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="h-12 animate-pulse rounded-xl bg-[rgba(11,59,46,0.06)]" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <p className="py-10 text-center text-sm font-medium" style={{ color: OMS_COLORS.caption }}>
          No task flow in this scope — widen the range or clear filters.
        </p>
      ) : (
        <div className="mt-3 grid gap-4 xl:grid-cols-[minmax(0,1fr)_240px]">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.ul
              key={levelKey}
              initial={{ opacity: 0, scale: 0.985, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 1.01, y: -8 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="min-w-0 space-y-1.5"
            >
              {entries.map((entry, index) => (
                <li key={entry.key}>
                  <button
                    type="button"
                    data-admin-row
                    onClick={() => drill(entry)}
                    aria-label={`Open ${entry.name}`}
                    className="grid w-full grid-cols-[auto_minmax(0,1.5fr)_minmax(0,1fr)_auto] items-center gap-3 rounded-xl border border-transparent px-2.5 py-2.5 text-left transition hover:border-[rgba(11,59,46,0.16)] hover:bg-[rgba(11,59,46,0.04)] focus-visible:ring-2 focus-visible:ring-[rgba(20,96,107,0.45)]"
                  >
                    <span className="w-5 text-center text-sm font-extrabold tabular-nums" style={{ color: OMS_COLORS.caption }}>
                      {index + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-extrabold tracking-tight" style={{ color: OMS_COLORS.tealDark }}>
                        {entry.name}
                      </span>
                      <span className="mt-0.5 block truncate text-[10.5px] font-semibold" style={{ color: OMS_COLORS.caption }}>
                        {entry.kind === 'engineer' ? (
                          <>
                            {formatNumber(entry.created)} app actions · {formatNumber(entry.completed)} completed ·{' '}
                            {formatNumber(entry.skipped)} skipped
                          </>
                        ) : (
                          <>
                            {formatNumber(entry.created)} created ·{' '}
                            {entry.completionPct === null
                              ? 'nothing scheduled'
                              : `${entry.completionPct}% completed · ${entry.pendingPct}% pending · ${entry.skipPct}% skipped of ${formatNumber(entry.scheduled)} scheduled`}
                          </>
                        )}
                      </span>
                    </span>
                    <span className="hidden sm:block">
                      <RateTrioBar
                        completionPct={entry.completionPct}
                        pendingPct={entry.pendingPct}
                        skipPct={entry.skipPct}
                      />
                    </span>
                    <span className="flex flex-col items-end gap-0.5">
                      <span className="flex items-center gap-1 text-[11px] font-bold tabular-nums" style={{ color: OMS_COLORS.caption }}>
                        <Users className="h-3 w-3" /> {formatNumber(entry.activeEngineers)}
                      </span>
                      <DeltaBadge delta={entry.deltaCompletionPct} phrase={phrase} suffix=" pts" />
                    </span>
                  </button>
                </li>
              ))}
            </motion.ul>
          </AnimatePresence>
          <Rails entries={entries} onSelect={drill} />
        </div>
      )}
    </section>
  );
}
